/**
 * <EtbisBadge>
 * ============
 * T.C. Ticaret Bakanlığı ETBİS sicil doğrulama rozeti — 6563 sayılı Elektronik
 * Ticaret Kanunu Md. 6 + Yönetmelik Md. 5 gereği sicil bilgisi sitede görünür
 * olmalıdır.
 *
 * 3 variant:
 *   - `compact` : Footer satır içi — "ETBİS · Kayıt: 15.05.2026 · Doğrula →"
 *   - `card`    : İletişim/Hakkımızda — beyaz kart + 96×96 QR + CTA buton
 *   - `full`    : KVKK/Yasal — geniş kart + açıklama + 128×128 QR + mevzuat
 *
 * Tasarım: Slate palette (CLAUDE.md), emerald-700 doğrulama spotu, min text
 * 14px (40+ yaş kuralı), focus ring slate-900, mobile touch 44×44.
 *
 * Veri kaynağı: KURUMSAL_KIMLIK.etbis (SSoT — kurumsalKimlik.ts)
 */

import Link from 'next/link';
import { CheckBadgeIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { KURUMSAL_KIMLIK } from '@/shared/constants/kurumsalKimlik';

export interface EtbisBadgeProps {
  variant?: 'compact' | 'card' | 'full';
  className?: string;
}

/** "2026-05-15" → "15.05.2026" (Türkçe gün.ay.yıl) */
function formatTrDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export function EtbisBadge({ variant = 'card', className = '' }: EtbisBadgeProps) {
  const etbis = KURUMSAL_KIMLIK.etbis;
  const sicilTr = formatTrDate(etbis.sicilTarihi);

  // ─── COMPACT (footer satır içi) ──────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-slate-600 ${className}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <CheckBadgeIcon
            className="w-4 h-4 text-emerald-700"
            aria-hidden="true"
            strokeWidth={1.75}
          />
          <span className="font-semibold text-slate-700">ETBİS</span>
        </span>
        <span className="text-slate-300" aria-hidden="true">·</span>
        <span className="tabular-nums">Kayıt: {sicilTr}</span>
        <span className="text-slate-300" aria-hidden="true">·</span>
        <a
          href={etbis.dogrulamaUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="ETBİS sicil sayfasında doğrula"
          className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-slate-900 hover:underline focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1 rounded"
        >
          Doğrula
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />
        </a>
      </div>
    );
  }

  // ─── FULL (KVKK / yasal sayfa) ───────────────────────────────────────────
  if (variant === 'full') {
    return (
      <section
        className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`}
        aria-labelledby="etbis-badge-full-title"
      >
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="text-[11px] font-bold text-slate-600 uppercase tracking-[0.12em] mb-1">
            Resmi Sicil Doğrulama
          </div>
          <h3
            id="etbis-badge-full-title"
            className="text-lg font-bold text-slate-900 flex items-center gap-2"
          >
            <CheckBadgeIcon
              className="w-5 h-5 text-emerald-700"
              aria-hidden="true"
              strokeWidth={1.75}
            />
            ETBİS Kayıtlı Hizmet Sağlayıcı
          </h3>
        </div>
        <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-start">
          <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>
              Riskmatik, T.C. Ticaret Bakanlığı <strong>Elektronik Ticaret Bilgi Sistemi
              (ETBİS)</strong>&apos;ne kayıtlı bir hizmet sağlayıcıdır. Sicil bilgileri Bakanlık
              sorgu sayfasından doğrulanabilir.
            </p>
            <dl className="grid grid-cols-1 gap-y-1.5 text-[13px]">
              <div className="flex gap-2">
                <dt className="font-semibold text-slate-900 min-w-[120px]">Sicil Tarihi:</dt>
                <dd className="tabular-nums">{sicilTr}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-semibold text-slate-900 min-w-[120px]">Site ID:</dt>
                <dd className="font-mono text-[12px] break-all text-slate-600">{etbis.siteId}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-semibold text-slate-900 min-w-[120px]">Yasal Dayanak:</dt>
                <dd>{etbis.mevzuatRef}</dd>
              </div>
            </dl>
            <a
              href={etbis.dogrulamaUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ETBİS sicil sayfasında doğrula"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 active:bg-black transition focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 min-h-[44px]"
            >
              Bakanlık Sorgu Sayfasında Doğrula
              <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
            </a>
            <small className="block text-[12px] text-slate-600 leading-relaxed">
              Mevzuat zorunluluğu: {etbis.mevzuatRef} ve Elektronik Ticarette Hizmet Sağlayıcı
              ve Aracı Hizmet Sağlayıcılar Hakkında Yönetmelik Md. 5 gereği bu sicil bilgisi
              sitede görünür olarak yayınlanmaktadır.
            </small>
          </div>
          <div className="flex flex-col items-center gap-2 shrink-0">
            <div className="p-2 bg-white border border-slate-200 rounded-lg">
              <img
                src={etbis.qrPath}
                alt="Riskmatik ETBİS sicil doğrulama QR kodu"
                width={128}
                height={128}
                loading="lazy"
                className="block"
              />
            </div>
            <span className="text-[11px] text-slate-600 font-medium">QR ile doğrula</span>
          </div>
        </div>
      </section>
    );
  }

  // ─── CARD (default — İletişim / Hakkımızda) ──────────────────────────────
  return (
    <section
      className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`}
      aria-labelledby="etbis-badge-card-title"
    >
      <div className="px-6 pt-6 pb-4 border-b border-slate-100">
        <div className="text-[11px] font-bold text-slate-600 uppercase tracking-[0.12em]">
          Resmi Belgeler
        </div>
        <h3
          id="etbis-badge-card-title"
          className="mt-1 text-base font-bold text-slate-900 flex items-center gap-2"
        >
          <CheckBadgeIcon
            className="w-5 h-5 text-emerald-700"
            aria-hidden="true"
            strokeWidth={1.75}
          />
          ETBİS Sicil Kaydı
        </h3>
      </div>
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="space-y-2 text-[13px] text-slate-700 leading-relaxed">
          <div className="text-slate-600">
            T.C. Ticaret Bakanlığı Elektronik Ticaret Bilgi Sistemi&apos;ne kayıtlı hizmet
            sağlayıcı.
          </div>
          <dl className="space-y-1">
            <div className="flex gap-2">
              <dt className="font-semibold text-slate-900">Sicil Tarihi:</dt>
              <dd className="tabular-nums">{sicilTr}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-semibold text-slate-900">Dayanak:</dt>
              <dd>{etbis.mevzuatRef}</dd>
            </div>
          </dl>
          <a
            href={etbis.dogrulamaUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ETBİS sicil sayfasında doğrula"
            className="inline-flex items-center gap-1.5 mt-1 text-[13px] font-semibold text-slate-900 hover:underline focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1 rounded min-h-[44px]"
          >
            Bakanlık sayfasında doğrula
            <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
        <div className="flex flex-col items-center gap-1.5 shrink-0">
          <div className="p-1.5 bg-white border border-slate-200 rounded-lg">
            <img
              src={etbis.qrPath}
              alt="Riskmatik ETBİS sicil doğrulama QR kodu"
              width={96}
              height={96}
              loading="lazy"
              className="block"
            />
          </div>
          <span className="text-[11px] text-slate-600 font-medium">QR ile doğrula</span>
        </div>
      </div>
    </section>
  );
}

export default EtbisBadge;
